import React from "react";
import { Link, LinkProps, SxProps, Theme, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

type LinkGradientProps = Omit<LinkProps, "component"> & {
  children: React.ReactNode;
  underlineHeight?: number;
  underlineInset?: string;
  showUnderlineOnHover?: boolean;
  sx?: SxProps<Theme>;
  to?: string;
  href?: string;
  component?: React.ElementType;
};

/**
 * Reusable Link component with link-like text color and gradient underline on hover
 */
const LinkGradient: React.FC<LinkGradientProps> = ({
  children,
  underlineHeight = 2,
  underlineInset = "10%",
  showUnderlineOnHover = true,
  sx,
  to,
  href,
  component,
  ...props
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  // Determine which component to use
  let linkComponent: React.ElementType | undefined;
  if (component) {
    linkComponent = component;
  } else if (to) {
    linkComponent = RouterLink;
  } else if (href) {
    linkComponent = "a";
  }
  
  const gradient = isDark
    ? "linear-gradient(90deg, #ffffff 0%, #a5b4fc 100%)"
    : "linear-gradient(90deg, #1e293b 0%, #3b82f6 100%)";
  
  // Use more visible colors
  const linkColor = isDark
    ? "#a5b4fc" // Brighter purple-blue for dark mode
    : "#2563eb"; // Brighter blue for light mode
  
  const linkColorHover = isDark
    ? "#c7d2fe" // Even brighter for hover in dark mode
    : "#1d4ed8"; // Darker blue for hover in light mode
  
  return (
    <Link
      component={linkComponent}
      to={to}
      href={href}
      {...props}
      sx={[
        (theme) => ({
          color: `${linkColor} !important`,
          WebkitTextFillColor: `${linkColor} !important`,
          textDecoration: "none",
          backgroundColor: "transparent !important",
          boxShadow: "none",
          transition: "color 0.2s ease",
          position: "relative",
          display: "inline-block",
          width: "fit-content",
          fontWeight: 600,
          backgroundImage: "none !important",
          backgroundClip: "unset !important",
          WebkitBackgroundClip: "unset !important",
          "&:hover": {
            color: `${linkColorHover} !important`,
            WebkitTextFillColor: `${linkColorHover} !important`,
            backgroundColor: "transparent !important",
            textDecoration: "none",
            boxShadow: "none",
            backgroundImage: "none !important",
          },
          "&:focus": {
            backgroundColor: "transparent !important",
            textDecoration: "none",
            outline: "none",
            boxShadow: "none",
            color: `${linkColorHover} !important`,
            WebkitTextFillColor: `${linkColorHover} !important`,
            backgroundImage: "none !important",
          },
          "&:active": {
            backgroundColor: "transparent !important",
            textDecoration: "none",
            boxShadow: "none",
            color: `${linkColorHover} !important`,
            WebkitTextFillColor: `${linkColorHover} !important`,
            backgroundImage: "none !important",
          },
          "&:visited": {
            color: `${linkColor} !important`,
            WebkitTextFillColor: `${linkColor} !important`,
            backgroundImage: "none !important",
          },
          ...(showUnderlineOnHover
            ? {
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  bottom: 0,
                  height: `${underlineHeight}px`,
                  width: "100%",
                  maxWidth: "100%",
                  backgroundImage: gradient,
                  backgroundColor: "transparent",
                  backgroundSize: "100% 100%",
                  backgroundRepeat: "no-repeat",
                  borderRadius: `${underlineHeight / 2}px`,
                  transform: "scaleX(0)",
                  transformOrigin: "center",
                  transition: "transform 0.2s ease",
                  pointerEvents: "none",
                  zIndex: 0,
                },
                "&:hover::after": {
                  transform: "scaleX(1)",
                },
              }
            : {}),
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Link>
  );
};

export default LinkGradient;

